/**
 * Permission predicates for Phase 2 Controladoria Jurídica resources (D-2.20).
 *
 * Rules (D-1.17 + D-1.18 + SP-4):
 *  - Group by resource: one named predicate per action per resource.
 *  - `s.role.ativo` is the floor for any operation.
 *  - `administracao` is NEVER an implicit super-permission — every predicate
 *    spells out which role booleans pass. This is the D-1.18 contract.
 *
 * Matrix confirmed by Carlson during plan 02-07 checkpoint (D-2.20 finalized):
 *  - Admin and jurídico are equivalent in create/update for all resources.
 *  - Soft delete + anonimização: jurídico OR admin (advogado pode arquivar
 *    casos antigos; gate dual). The hard "only admin" stance was relaxed.
 *  - Comercial: list/read/create on processo only (workflow: pre-attribution
 *    capture). The "comercial loses write after advogado responsável is
 *    assigned" rule is row-level state and lives in the processo service
 *    layer (plan 02-08), not in this matrix.
 *  - Comercial and financeiro: no access to publicacao, contato, tarefa,
 *    audiencia, processo_parte, busca.
 *
 * Wave 2/3/4 routes import named permissions object directly:
 *   import { processoPermissions } from '@/shared/permissions/controladoria';
 */
import type { SessionContext } from './types';
import { isUniversalModuleUser } from './universal';

// Shared predicate fragments — keep readability + single source of edit.
const hasJuridicoOrAdminRole = (s: SessionContext) => s.role.juridico || s.role.administracao;

export const publicacaoPermissions = {
  list: (s: SessionContext) => s.role.ativo && hasJuridicoOrAdminRole(s),
  read: (s: SessionContext) => s.role.ativo && hasJuridicoOrAdminRole(s),
  update: (s: SessionContext) => s.role.ativo && hasJuridicoOrAdminRole(s), // mark lida / arquivada
};

export const processoPermissions = {
  list: isUniversalModuleUser,
  read: isUniversalModuleUser,
  create: isUniversalModuleUser,
  update: isUniversalModuleUser,
  softDelete: isUniversalModuleUser,
  anonimizar: isUniversalModuleUser,
  // Dedicated picker operation preserves the endpoint's minimum DTO contract.
  pickerList: isUniversalModuleUser,
};

export const contatoPermissions = {
  list: isUniversalModuleUser,
  read: isUniversalModuleUser,
  create: isUniversalModuleUser,
  update: isUniversalModuleUser,
  softDelete: isUniversalModuleUser,
  anonimizar: isUniversalModuleUser,
  // Phase 184 — the Oportunidade create form's
  // ContatoPicker hits `/api/v1/controladoria/contatos/pickers/representante`, a
  // NAME-ONLY tenant-scoped search returning `{contatoid, contatonome}`.
  // The separate operation preserves that minimum DTO boundary.
  pickerRead: isUniversalModuleUser,
};

export const processoPartePermissions = {
  list: isUniversalModuleUser,
  create: isUniversalModuleUser,
  // Phase 98 (PARTEDIT-01/06, D-04): edit/delete a parte on an existing
  // processo. Identical rule to `.create` — ativo && (juridico || admin).
  // Phase 216 replaces the former departmental gate with the active-member floor.
  update: isUniversalModuleUser,
  delete: isUniversalModuleUser,
};

export const tarefaPermissions = {
  list: isUniversalModuleUser,
  read: isUniversalModuleUser,
  create: isUniversalModuleUser,
  update: isUniversalModuleUser,
};

/**
 * TipoTarefa permissions (Phase 07 plan 07-02 / D-04).
 *
 * Unlike the other Controladoria resources, the Configurações area is
 * READABLE by ANY active escritório user (list/read = `s.role.ativo` only —
 * no juridico/comercial gate). Mutations (create/update/remove) are
 * administracao-only. `administracao` is spelled out explicitly per the
 * D-1.18 contract (never an implicit super-permission).
 */
export const tipoTarefaPermissions = {
  list: (s: SessionContext) => s.role.ativo,
  read: (s: SessionContext) => s.role.ativo,
  create: (s: SessionContext) => s.role.ativo && s.role.administracao,
  update: (s: SessionContext) => s.role.ativo && s.role.administracao,
  remove: (s: SessionContext) => s.role.ativo && s.role.administracao,
  // WR-05: the responsáveis picker enumerates the full active-user roster of
  // the escritório. Its ONLY consumer is the admin-only create/edit form, so
  // gate it on the same authority that may AUTHOR a tipo de tarefa
  // (administracao) — least privilege. A comercial/financeiro active user has
  // no legitimate need to enumerate the roster via this endpoint. Still
  // tenant-scoped (no cross-escritório leak); this closes the data-exposure
  // gap without touching the admin create/edit flow.
  responsaveis: (s: SessionContext) => s.role.ativo && s.role.administracao,
};

export const audienciaPermissions = {
  list: (s: SessionContext) => s.role.ativo && hasJuridicoOrAdminRole(s),
  read: (s: SessionContext) => s.role.ativo && hasJuridicoOrAdminRole(s),
  create: (s: SessionContext) => s.role.ativo && hasJuridicoOrAdminRole(s),
  update: (s: SessionContext) => s.role.ativo && hasJuridicoOrAdminRole(s),
};

/**
 * Atendimento (processo timeline) permissions — Phase 23 (TML-08 / TML-09).
 *
 * Adds a `remove` predicate: the timeline resource supports DELETE. All four
 * Phase 216 gates all four actions on the active-member floor. The former
 * Anotações permissions were removed with the Anotações feature; the timeline
 * supersedes it.
 */
export const atendimentoPermissions = {
  list: isUniversalModuleUser,
  create: isUniversalModuleUser,
  update: isUniversalModuleUser,
  remove: isUniversalModuleUser,
};

export const buscaPermissions = {
  query: (s: SessionContext) => s.role.ativo && hasJuridicoOrAdminRole(s),
};
